const REDIS_URL = process.env.EXPO_PUBLIC_UPSTASH_REDIS_REST_URL;
const REDIS_TOKEN = process.env.EXPO_PUBLIC_UPSTASH_REDIS_REST_TOKEN;

const KEY_PREFIX = "groupchat:typing";
const DEFAULT_TTL_SECONDS = 10;

const __DEV__ = process.env.NODE_ENV !== "production";

const isConfigured = () => Boolean(REDIS_URL && REDIS_TOKEN);

async function executeCommand(command: (string | number)[]) {
  if (!isConfigured()) {
    if (!REDIS_URL || !REDIS_TOKEN) {
      console.warn("Upstash Redis env vars not configured");
    }
    return null;
  }
  const response = await fetch(REDIS_URL!, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${REDIS_TOKEN}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(command.map((value) => value.toString())),
  });
  return response.json();
}

async function executePipeline(commands: (string | number)[][]) {
  if (!isConfigured()) {
    if (!REDIS_URL || !REDIS_TOKEN) {
      console.warn("Upstash Redis env vars not configured");
    }
    return null;
  }
  const response = await fetch(`${REDIS_URL}/pipeline`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${REDIS_TOKEN}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      commands.map((command) => command.map((value) => value.toString()))
    ),
  });
  return response.json();
}

const buildKey = (groupId: string) => `${KEY_PREFIX}:${groupId}`;

export async function setGroupTypingUser(groupId: string, userId: string) {
  try {
    const key = buildKey(groupId);
    const payload = Date.now().toString();
    await executePipeline([
      ["HSET", key, userId, payload],
      ["EXPIRE", key, DEFAULT_TTL_SECONDS],
    ]);
  } catch (error) {
    console.warn("Failed to set typing user", error);
  }
}

export async function removeGroupTypingUser(groupId: string, userId: string) {
  try {
    const key = buildKey(groupId);
    await executeCommand(["HDEL", key, userId]);
  } catch (error) {
    console.warn("Failed to remove typing user", error);
  }
}

export type TypingUserEntry = {
  userId: string;
  timestamp: number;
};

export async function fetchGroupTypingUsers(
  groupId: string
): Promise<TypingUserEntry[]> {
  try {
    const key = buildKey(groupId);
    const response = await executeCommand(["HGETALL", key]);
    const result = response?.result;
    if (!Array.isArray(result)) {
      return [];
    }
    const entries: TypingUserEntry[] = [];
    for (let i = 0; i < result.length; i += 2) {
      const userId = result[i];
      const value = result[i + 1];
      if (!userId || !value) continue;
      try {
        const timestamp = Number(value);
        if (Number.isNaN(timestamp)) {
          continue;
        }
        entries.push({
          userId,
          timestamp,
        });
      } catch (error) {
        console.warn("Failed to parse typing entry", error);
      }
    }
    return entries;
  } catch (error) {
    console.warn("Failed to fetch typing users", error);

    return [];
  }
}

export async function seedGroupTypingUsers(groupId: string, userIds: string[]) {
  if (!__DEV__) {
    return;
  }
  try {
    const key = buildKey(groupId);
    const now = Date.now();
    const commands: (string | number)[] = ["HSET", key];
    userIds.forEach((userId) => {
      commands.push(userId, now.toString());
    });

    await executePipeline([commands, ["EXPIRE", key, DEFAULT_TTL_SECONDS]]);
  } catch (error) {
    console.warn("Failed to seed typing users", error);
  }
}
